import React, { useState, useEffect } from 'react';
import { FileText, AlertCircle, Loader, CheckCircle, Download, Calendar } from "lucide-react";
import { usePageHeader } from '../../contexts/PageHeaderContext';
import p11Service from '../../api/services/p11Service';
import timesheetService from "../../api/services/timesheetService";
import useEmployeeProfile from "../../hooks/useEmployeeProfile";
import formatDate from '../../utils/formatDate';

const EmployeeP11 = ({ hideHeader = false }) => {
  const { setHeader } = usePageHeader();
  const [activeSubTab, setActiveSubTab] = useState('p11d');

  // P11D state
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [p11Records, setP11Records] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generateError, setGenerateError] = useState(null);
  const [generateSuccess, setGenerateSuccess] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [isLoadingYears, setIsLoadingYears] = useState(true);
  const [fiscalYears, setFiscalYears] = useState([]);
  const [selectedFiscalYear, setSelectedFiscalYear] = useState('');

  const {
    employeeNo,
    displayName: employeeName,
    isLoading: isLoadingProfile,
    error: profileError,
  } = useEmployeeProfile();

  useEffect(() => {
    if (!hideHeader) {
      setHeader({
        icon: FileText,
        title: "P11 Records",
        subtitle: "View your P11 deductions working sheet records",
      });
    }
    return () => {
      if (!hideHeader) setHeader({ icon: null, title: "", subtitle: "" });
    };
  }, [hideHeader, setHeader]);

  useEffect(() => {
    const fetchP11Records = async () => {
      if (!employeeNo) return;
      setIsLoading(true);
      setError(null);
      try {
        const response = await timesheetService.getFiscalYears();
        const years = response.data?.value || [];
        const activeYear = years.find((y) => y.active === true);
        const currentFiscalYear = activeYear?.fiscalYear;
        const records = await p11Service.getP11Records(
          employeeNo,
          currentFiscalYear,
        );
        setP11Records(records);
      } catch (err) {
        let msg = "We're unable to load your P11 records at the moment. ";
        if (err.response?.status === 404 || err.message?.includes("not found")) {
          msg += "No P11 records were found for your employee account.";
        } else if (err.response?.status === 401 || err.response?.status === 403) {
          msg += "You don't have permission to access these records.";
        } else if (err.response?.status >= 500) {
          msg += "Our system is experiencing technical difficulties. Please try again later.";
        } else {
          msg += "Please try refreshing the page or contact HR support.";
        }
        setError(msg);
      } finally {
        setIsLoading(false);
      }
    };
    fetchP11Records();
  }, [employeeNo]);

  useEffect(() => {
    const fetchFiscalYears = async () => {
      setIsLoadingYears(true);
      try {
        const years = await p11Service.getClosedFiscalYears();
        setFiscalYears(years);
        if (years && years.length > 0) {
          const latest = years[0].name || years[0].fiscalYear || years[0];
          setSelectedFiscalYear(latest);
        }
      } catch (err) {
        console.error('Error fetching fiscal years:', err);
      } finally {
        setIsLoadingYears(false);
      }
    };
    fetchFiscalYears();
  }, []);

  const handleGenerateP11 = async () => {
    if (!employeeNo) return;
    if (!selectedFiscalYear) {
      setGenerateError('Please select a fiscal year.');
      return;
    }
    setIsGenerating(true);
    setGenerateError(null);
    setGenerateSuccess(false);
    setPdfUrl(null);
    try {
      const base64 = await p11Service.generateP11Document(employeeNo, selectedFiscalYear);
      const byteCharacters = atob(base64.replace(/^data:application\/pdf;base64,/, ''));
      const byteArray = new Uint8Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) byteArray[i] = byteCharacters.charCodeAt(i);
      const blob = new Blob([byteArray], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      setPdfUrl(url);
      setGenerateSuccess(true);
    } catch (err) {
      setGenerateError('Failed to generate P11D document. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDownload = () => {
    if (!pdfUrl) return;
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = `P11D_${employeeNo}_${selectedFiscalYear.replace(/\//g, '-')}.pdf`;
    document.body.appendChild(link);
    link.click();
    setTimeout(() => { document.body.removeChild(link); }, 100);
  };

  // Decode BC encoded strings e.g. "A_x002E__x0020_Assets..." → "A. Assets..."
  const decodeP11Type = (str) => {
    if (!str) return '—';
    return str
      .replace(/_x002E_/g, '.')
      .replace(/_x0020_/g, ' ')
      .replace(/_x0028_/g, '(')
      .replace(/_x0029_/g, ')')
      .replace(/_x002F_/g, '/')
      .replace(/_x0026_/g, '&')
      .trim();
  };

  // Format date M/D/YYYY
  const fmtDate = (d) => {
    if (!d || d === '0001-01-01') return '—';
    const dt = new Date(d);
    return `${dt.getMonth() + 1}/${dt.getDate()}/${dt.getFullYear()}`;
  };

  // Format currency
  const fmt = (val) => {
    if (!val && val !== 0) return '—';
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(val);
  };

  // P11D(b) state
  const [isLoadingP11db, setIsLoadingP11db] = useState(false);
  const [p11dbError, setP11dbError] = useState(null);
  const [p11dbRecords, setP11dbRecords] = useState([]);

  useEffect(() => {
    const fetchP11DbRecords = async () => {
      if (!employeeNo || activeSubTab !== 'p11db') return;
      setIsLoadingP11db(true);
      setP11dbError(null);
      try {
        const records = await p11Service.getP11DbRecords(employeeNo);
        setP11dbRecords(records);
      } catch (err) {
        let msg = "We're unable to load your P11D(b) records at the moment. ";
        if (err.response?.status === 404 || err.message?.includes("not found")) {
          msg += "No P11D(b) records were found for your employee account.";
        } else if (err.response?.status === 401 || err.response?.status === 403) {
          msg += "You don't have permission to access these records.";
        } else if (err.response?.status >= 500) {
          msg += "Our system is experiencing technical difficulties. Please try again later.";
        } else {
          msg += "Please try refreshing the page or contact HR support.";
        }
        setP11dbError(msg);
      } finally {
        setIsLoadingP11db(false);
      }
    };
    fetchP11DbRecords();
  }, [employeeNo, activeSubTab]);

  const subTabs = [
    { id: 'p11d', label: 'P11D' },
    { id: 'p11db', label: 'P11D(b)' },
  ];

  return (
    <div className="max-w-6xl mx-auto">
      {/* Sub-tabs */}
      <div className="border-b border-gray-200 px-6 mb-3">
        <nav className="-mb-px flex space-x-8">
          {subTabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveSubTab(tab.id)}
              className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeSubTab === tab.id
                  ? 'border-emerald-500 text-emerald-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* ── P11D Tab ── */}
      {activeSubTab === 'p11d' && (
        <div className="p-6">
          {isLoadingProfile && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6 flex items-center gap-3">
              <Loader className="w-5 h-5 text-blue-600 animate-spin" />
              <p className="text-blue-900">Loading employee profile...</p>
            </div>
          )}

          {/* Header Card */}
          <div className="bg-gradient-to-r from-emerald-500 to-teal-600 rounded-2xl shadow-lg p-8 mb-6 text-white">
            <div className="flex items-start gap-4">
              <div className="bg-white/20 p-3 rounded-xl backdrop-blur-sm">
                <FileText className="w-8 h-8" />
              </div>
              <div className="flex-1">
                <h1 className="text-3xl font-bold mb-2">P11D Records</h1>
                <p className="text-emerald-100 text-lg">View your P11D benefits and expenses records</p>
                {employeeName && (
                  <p className="text-emerald-200 mt-2">Employee: {employeeName} ({employeeNo})</p>
                )}
              </div>
            </div>
          </div>

          {/* Info Card */}
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6 mb-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-emerald-900">
                <p className="font-semibold mb-1">What is a P11D?</p>
                <p className="mb-1">A P11D is a form submitted by employers to HMRC reporting expenses and benefits provided to employees and directors.</p>
                <p className="text-emerald-800">It includes information about company cars, private medical insurance, and other taxable benefits in kind.</p>
              </div>
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {/* Loading */}
          {isLoading ? (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-6 flex items-center gap-3">
              <Loader className="w-5 h-5 text-blue-600 animate-spin" />
              <p className="text-blue-900">Loading P11D records...</p>
            </div>
          ) : !error && (
            <>
              {/* Table */}
              <div className="bg-white rounded-xl shadow-md overflow-hidden mb-6">
                <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200 sticky top-0 z-10">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">P11D Type</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fiscal Year</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">From Date</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">To Date</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Cost</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Cash Equivalent Amount</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {p11Records.length > 0 ? p11Records.map((record, index) => (
                        <tr key={index} className="hover:bg-gray-50 transition-colors">
                          <td className="px-4 py-4 text-sm font-medium text-gray-900 max-w-[200px]">
                            {decodeP11Type(record.p11DType)}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
                            {record.fiscalYear || '—'}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
                            {formatDate(record.fromDate)}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
                            {formatDate(record.toDate)}
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-600">
                            {record.description || record.assetDescription || '—'}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm font-semibold text-gray-900 text-right">
                            {fmt(record.cost)}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm font-semibold text-gray-900 text-right">
                            {fmt(record.amount)}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm font-semibold text-gray-900 text-right">
                            {fmt(record.cashEquivOrRelevantAmt)}
                          </td>
                        </tr>
                      )) : (
                        <tr>
                          <td colSpan={8} className="px-4 py-10 text-center text-gray-500">
                            <FileText className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                            <p className="font-medium">No P11D records found</p>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Generate Document Card */}
              <div className="bg-white rounded-xl shadow-md p-8 mb-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Generate P11D Document</h2>
                <p className="text-gray-600 mb-6">
                  Select a fiscal year and click the button below to generate your P11D document. The document will be created in PDF format and can be viewed or downloaded.
                </p>

                {/* Fiscal Year Selector */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Calendar className="w-4 h-4 inline mr-2" />
                    Select Fiscal Year
                  </label>
                  {isLoadingYears ? (
                    <div className="flex items-center gap-2 text-gray-500">
                      <Loader className="w-4 h-4 animate-spin" />
                      <span>Loading fiscal years...</span>
                    </div>
                  ) : fiscalYears.length > 0 ? (
                    <select
                      value={selectedFiscalYear}
                      onChange={(e) => setSelectedFiscalYear(e.target.value)}
                      className="w-full md:w-96 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none transition-all"
                    >
                      <option value="">-- Select a fiscal year --</option>
                      {fiscalYears.map((year, index) => {
                        const yearValue = year.name || year.fiscalYear || year.year || year;
                        const yearDisplay = year.displayName || year.name || year.fiscalYear || year.year || year;
                        return (
                          <option key={index} value={yearValue}>{yearDisplay}</option>
                        );
                      })}
                    </select>
                  ) : (
                    <p className="text-gray-500">No closed fiscal years available</p>
                  )}
                </div>

                <button
                  onClick={handleGenerateP11}
                  disabled={isGenerating || isLoadingProfile || !employeeNo || !selectedFiscalYear || isLoadingYears}
                  className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white px-8 py-4 rounded-xl font-semibold text-lg hover:from-emerald-600 hover:to-teal-700 transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-3"
                >
                  {isGenerating ? (
                    <>
                      <Loader className="w-5 h-5 animate-spin" />
                      <span>Generating...</span>
                    </>
                  ) : (
                    <>
                      <FileText className="w-5 h-5" />
                      <span>Generate P11D Document</span>
                    </>
                  )}
                </button>
              </div>

              {/* Generate Success */}
              {generateSuccess && !generateError && (
                <div className="bg-green-50 border border-green-200 rounded-xl p-6 mb-6">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="font-semibold text-green-900 mb-2">P11D Generated Successfully!</p>
                      <p className="text-sm text-green-800 mb-4">
                        Your P11D document for fiscal year {selectedFiscalYear} has been generated. You can view it below or download it to your device.
                      </p>
                      <button
                        onClick={handleDownload}
                        className="bg-green-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-green-700 transition-colors flex items-center gap-2"
                      >
                        <Download className="w-4 h-4" />
                        Download P11D
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Generate Error */}
              {generateError && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-6 mb-6">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                    <p className="text-sm text-red-800">{generateError}</p>
                  </div>
                </div>
              )}

              {/* PDF Preview */}
              {pdfUrl && (
                <div className="bg-white rounded-xl shadow-md p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Document Preview</h3>
                  <iframe src={pdfUrl} className="w-full h-[800px] border border-gray-200 rounded-lg" title="P11D Document Preview" />
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ── P11D(b) Tab ── */}
      {activeSubTab === 'p11db' && (
        <div className="p-6">
          {/* Header Card */}
          <div className="bg-gradient-to-r from-emerald-500 to-teal-600 rounded-2xl shadow-lg p-8 mb-6 text-white">
            <div className="flex items-start gap-4">
              <div className="bg-white/20 p-3 rounded-xl backdrop-blur-sm">
                <FileText className="w-8 h-8" />
              </div>
              <div className="flex-1">
                <h1 className="text-3xl font-bold mb-2">P11D(b) Records</h1>
                <p className="text-emerald-100 text-lg">View your P11D(b) Class 1A National Insurance summary</p>
                {employeeName && (
                  <p className="text-emerald-200 mt-2">Employee: {employeeName} ({employeeNo})</p>
                )}
              </div>
            </div>
          </div>

          {/* Info Card */}
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6 mb-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-emerald-900">
                <p className="font-semibold mb-1">What is a P11D(b)?</p>
                <p className="mb-1">A P11D(b) is used by employers to declare the total amount of Class 1A National Insurance contributions due on expenses and benefits provided to employees.</p>
                <p className="text-emerald-800">It summarises all P11D forms submitted and shows the total Class 1A NIC liability for the tax year.</p>
              </div>
            </div>
          </div>

          {/* Error */}
          {p11dbError && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
              <p className="text-sm text-red-800">{p11dbError}</p>
            </div>
          )}

          {/* Loading */}
          {isLoadingP11db ? (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-6 flex items-center gap-3">
              <Loader className="w-5 h-5 text-blue-600 animate-spin" />
              <p className="text-blue-900">Loading P11D(b) records...</p>
            </div>
          ) : !p11dbError && (
            <div className="bg-white rounded-xl shadow-md overflow-hidden mb-6">
              <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200 sticky top-0 z-10">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Code</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Pay Unit</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Annual Salary</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Year to Date</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {p11dbRecords.length > 0 ? p11dbRecords.map((record, index) => (
                      <tr key={index} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {record.code || '—'}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
                          {record.type || '—'}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
                          {record.payUnit || '—'}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm font-semibold text-gray-900 text-right">
                          {fmt(record.amount)}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm font-semibold text-gray-900 text-right">
                          {record.annualSalary > 0 ? fmt(record.annualSalary) : '—'}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm font-semibold text-gray-900 text-right">
                          {fmt(record.yeartoDate)}
                        </td>
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan={6} className="px-4 py-10 text-center text-gray-500">
                          <FileText className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                          <p className="font-medium">No P11D(b) records found</p>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

        </div>
      )}
    </div>
  );
};

export default EmployeeP11;
